import { useState } from "react";
import CardDetailLayout from "../components/CardDetailLayout";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { AlertCircle, Settings } from "lucide-react";
import PublishButton from "../components/PublishButton";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { Button } from "@/modern/components/ui/button";
import { OpenAIKeyModal } from "../components/OpenAIKeyModal";
import { useBoardPermissions } from "../hooks/useBoardPermissions";

const AIPromptDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine which type of prompt this is
  const inspectionType = subcategory.inspectionType;
  const advisorType = subcategory.advisorType;
  const cardType = subcategory.cardType;
  const fieldAdvisorCardType = subcategory.fieldAdvisorCardType;

  // Validate that we have the required properties
  if (!inspectionType && !advisorType) {
    console.error("AIPromptDetail requires either inspectionType or advisorType", subcategory);
    return (
      <div className="p-6">
        <p className="text-destructive">Configuration error: Missing prompt type information</p>
      </div>
    );
  }

  if (inspectionType && !cardType) {
    console.error("AIPromptDetail with inspectionType requires cardType", subcategory);
    return (
      <div className="p-6">
        <p className="text-destructive">Configuration error: Missing card type information</p>
      </div>
    );
  }

  if (advisorType && !fieldAdvisorCardType) {
    console.error("AIPromptDetail with advisorType requires fieldAdvisorCardType", subcategory);
    return (
      <div className="p-6">
        <p className="text-destructive">Configuration error: Missing field advisor card type information</p>
      </div>
    );
  }

  // Subscribe to board preferences changes to trigger rerender if open api key flag changes
  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => {
      return record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey;
    }
  });

  // @ts-expect-error - userPreferences is dynamically set in bryntumContext
  const hasOpenAIKey = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";

  // Use shared permission hook
  const { canWrite } = useBoardPermissions();

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Publish Button Section */}
      {hasOpenAIKey && (
        <PublishButton inspectionType={inspectionType} advisorType={advisorType} />
      )}

      {/* OpenAI Key Warning */}
      {!hasOpenAIKey && (
        <div className="mx-6 mt-6 mb-0 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">OpenAI API Key Required</h4>
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  To configure AI Prompt settings, you need to set up your OpenAI API key first.
                  {canWrite ? " Click the button to configure your API key." : " Only the Board Owner can configure the API key."}
                </p>
              </div>
            </div>
            {canWrite && (
              <Button onClick={() => setIsModalOpen(true)} size="sm" variant="default" className="flex-shrink-0">
                <Settings className="h-4 w-4 mr-2" />
                Configure Key
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <CardDetailLayout subcategory={subcategory} disableEditing={!hasOpenAIKey} />
      </div>

      {/* OpenAI API Key Configuration Modal */}
      <OpenAIKeyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default AIPromptDetail;
